/**
 * Feedback Analytics Card Component
 * Displays analytics overview for feedback data
 */

import React from 'react';
import { FeedbackAnalytics, FeedbackType } from '../../../types/feedback';

interface FeedbackAnalyticsCardProps {
  analytics: FeedbackAnalytics;
  isLoading: boolean;
}

const FeedbackAnalyticsCard: React.FC<FeedbackAnalyticsCardProps> = ({
  analytics,
  isLoading
}) => {
  const getTypeLabel = (type: FeedbackType): string => {
    const labels: Record<FeedbackType, string> = {
      'quick-reaction': 'Quick Reactions',
      'page-comment': 'Page Comments',
      'element-feedback': 'Element Feedback',
      'flow-rating': 'Flow Ratings',
      'bug-report': 'Bug Reports',
      'feature-request': 'Feature Requests',
      'general': 'General Feedback'
    };
    return labels[type];
  };

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg border border-stone-200">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-stone-200 rounded w-48"></div>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-20 bg-stone-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <h3 className="text-lg font-medium text-slate-800 mb-2">
            Total Feedback
          </h3>
          <p className="text-3xl font-bold text-terracotta-600">
            {analytics.totalCount}
          </p>
        </div>

        {analytics.averageRating && (
          <div className="bg-white p-6 rounded-lg border border-stone-200">
            <h3 className="text-lg font-medium text-slate-800 mb-2">
              Average Rating
            </h3>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-slate-800">
                {analytics.averageRating.toFixed(1)}
              </p>
              <div className="flex text-amber-400">
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    className={i < Math.round(analytics.averageRating!) ? 'fill-current' : 'text-stone-300'}
                  >
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" fill="currentColor" />
                  </svg>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <h3 className="text-lg font-medium text-slate-800 mb-2">
            Top Priority Items
          </h3>
          <p className="text-3xl font-bold text-coral-600">
            {analytics.byPriority.high + analytics.byPriority.urgent}
          </p>
        </div>
      </div>

      {/* Feedback by Type */}
      <div className="bg-white p-6 rounded-lg border border-stone-200">
        <h3 className="text-lg font-medium text-slate-800 mb-4">
          Feedback by Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(analytics.byType).map(([type, count]) => {
            if (count === 0) return null;
            return (
              <div key={type} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                <span className="text-sm text-slate-700">
                  {getTypeLabel(type as FeedbackType)}
                </span>
                <span className="text-sm font-medium text-slate-800">
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Pages */}
      {analytics.topPages.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-stone-200">
          <h3 className="text-lg font-medium text-slate-800 mb-4">
            Top Pages by Feedback
          </h3>
          <div className="space-y-3">
            {analytics.topPages.slice(0, 5).map((page, index) => (
              <div key={page.url} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-500 w-4">
                    {index + 1}
                  </span>
                  <span className="text-sm text-slate-700 truncate max-w-xs">
                    {new URL(page.url).pathname}
                  </span>
                </div>
                <span className="text-sm font-medium text-slate-800">
                  {page.count} items
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Distribution */}
      <div className="bg-white p-6 rounded-lg border border-stone-200">
        <h3 className="text-lg font-medium text-slate-800 mb-4">
          Status Distribution
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(analytics.byStatus).map(([status, count]) => {
            if (count === 0) return null;
            const statusColors = {
              new: 'bg-blue-50 text-blue-700',
              reviewing: 'bg-amber-50 text-amber-700',
              'in-progress': 'bg-purple-50 text-purple-700',
              resolved: 'bg-sage-50 text-sage-700',
              closed: 'bg-stone-50 text-stone-700',
              archived: 'bg-stone-50 text-stone-500'
            };
            
            return (
              <div 
                key={status} 
                className={`p-3 rounded-lg text-center ${statusColors[status as keyof typeof statusColors]}`}
              >
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm capitalize">{status.replace('-', ' ')}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FeedbackAnalyticsCard;